/**
 * Make a phone photo small enough to upload on a weak connection.
 *
 * A picture of an ID card or a cheque taken on a modern phone is 3–8 MB. At the upload speeds
 * people actually had in the last meeting, that is minutes per file, and nothing on screen said
 * so — which is why one consultant refreshed and lost the lot. Re-encoded to 1600px on the long
 * edge it is usually 200–400 KB: the same document, legible to the eye and to the extractor,
 * ten to twenty times faster to send.
 *
 * BEST EFFORT, ALWAYS. Not an image, no canvas, a decode failure, or a "smaller" result that is
 * actually bigger — every one of those returns the original file untouched. Shrinking is a
 * speed-up, never a precondition for uploading.
 */
const MAX_EDGE = 1600
const QUALITY = 0.82
const SKIP_BELOW = 400 * 1024   // already small enough that re-encoding buys nothing

export default async function shrinkImage(file) {
  if (!file || !file.type?.startsWith('image/')) return file
  if (file.type === 'image/gif') return file       // re-encoding would drop the animation
  if (file.size <= SKIP_BELOW) return file

  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', QUALITY))
    if (!blob || blob.size >= file.size) return file

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return file
  }
}
